import React, { useState, useEffect, useMemo } from "react";
import {
  makeStyles,
  useTheme,
  useMediaQuery,
  Paper,
  Grid,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  InputAdornment,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
} from "@material-ui/core";
import { Formik, Form, Field, useFormikContext } from "formik";
import {
  PLAN_FEATURE_GROUPS,
  MARQUEE_BADGES,
  currencySymbol,
  intervalSuffix,
} from "./planFeatureCatalog";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  CheckCircle as CheckIcon,
  People as PeopleIcon,
  Sms as ConnectionsIcon,
  ListAlt as QueuesIcon,
  AttachMoney as PriceIcon,
  LocalOffer as PlanIcon,
} from "@material-ui/icons";

import { toast } from "react-toastify";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";

// ───── Mobile design tokens ─────
const SP = { xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24, xxl: 32 };
const TT = { min: 44, primary: 48, hero: 56 };
const MOBILE_INPUT_PROPS = { style: { fontSize: 16 } };

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accent = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const hair = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";
  return {
    root: {
      width: "100%",
      padding: 0,
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 16,
      flexWrap: "wrap",
      gap: 12,
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    // Brand-tinted icon pill replaces the orange→peach gradient block.
    headerIcon: {
      width: 38,
      height: 38,
      borderRadius: 11,
      background: isDark ? "rgba(46,227,122,0.12)" : "rgba(37,211,102,0.12)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& .MuiSvgIcon-root": {
        fontSize: 20,
        color: isDark ? "#7df0a8" : "#128c7e",
      },
    },
    headerTitle: {
      fontSize: 17,
      fontWeight: 700,
      lineHeight: 1.2,
      color: isDark ? "#f1f5f9" : "#0f172a",
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    headerSubtitle: {
      fontSize: 12.5,
      fontWeight: 500,
      color: isDark ? "rgba(255,255,255,0.55)" : "#94a3b8",
      marginTop: 2,
    },
    countChip: {
      fontFamily: "Roboto, sans-serif",
      fontWeight: 700,
      fontSize: 11,
      letterSpacing: 0.3,
      lineHeight: 1,
      backgroundColor: isDark ? "rgba(46,227,122,0.14)" : "rgba(37,211,102,0.14)",
      color: isDark ? "#7df0a8" : "#128c7e",
      borderRadius: 999,
      padding: "0 8px",
      height: 22,
      minWidth: 22,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    },
    addBtn: {
      background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
      color: "#fff",
      fontWeight: 700,
      padding: "8px 18px",
      borderRadius: 10,
      boxShadow: "0 6px 14px rgba(37, 211, 102, 0.28)",
      textTransform: "none",
      "&:hover": {
        background: "linear-gradient(135deg, #1ebe5d 0%, #075e54 100%)",
      },
    },
    searchBar: {
      padding: 14,
      marginBottom: 14,
      borderRadius: 14,
      border: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(15,23,42,0.06)",
      background: isDark ? "#0F1210" : "#fff",
      boxShadow: "none",
    },
    inputControl: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        background: isDark ? "#131614" : "#f8fafc",
        fontSize: 14,
        fontWeight: 500,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark
          ? "rgba(255,255,255,0.08)"
          : "rgba(15,23,42,0.08)",
      },
    },
    // CRM card-stack v3 — hairline border, 16 radius, soft shadow,
    // 4px WhatsApp green top stripe via planTopBar. Replaces the
    // previous translateY hover with a calmer brand-tint border lift.
    planCard: {
      position: "relative",
      borderRadius: 16,
      border: `1px solid ${hair}`,
      background: isDark ? "#13181a" : "#ffffff",
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px rgba(0,0,0,0.32)"
        : "0 1px 0 rgba(15,22,20,0.02), 0 8px 24px rgba(15,22,20,0.04)",
      transition: "transform 0.18s cubic-bezier(0.16,1,0.3,1), box-shadow 0.18s ease, border-color 0.18s ease",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      "&:hover": {
        transform: "translateY(-2px)",
        borderColor: isDark ? "rgba(46,227,122,0.28)" : "rgba(18,140,126,0.28)",
        boxShadow: isDark
          ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 14px 32px rgba(0,0,0,0.40)"
          : "0 1px 0 rgba(15,22,20,0.02), 0 14px 32px rgba(15,22,20,0.08)",
      },
    },
    planCardHighlight: {
      borderColor: isDark ? "rgba(46,227,122,0.55)" : "#25d366",
      borderWidth: 2,
    },
    // 4px brand-green top stripe (was 3px rainbow).
    planTopBar: {
      height: 4,
      background: "#25d366",
    },
    planHeader: {
      padding: `${SP.md}px ${SP.md}px ${SP.xs}px ${SP.md}px`,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: SP.xs,
      [theme.breakpoints.down("xs")]: {
        padding: `${SP.md}px ${SP.md}px ${SP.xs}px ${SP.md}px`,
      },
    },
    planHeaderBody: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
      minWidth: 0,
      flex: 1,
    },
    // Accent meta — uppercase brand-green tier label, 10.5/0.22em.
    planTier: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      lineHeight: 1.2,
    },
    planName: {
      fontSize: 18,
      fontWeight: 700,
      color: fg,
      lineHeight: 1.25,
      letterSpacing: "-0.005em",
    },
    visibilityChip: {
      fontWeight: 700,
      fontSize: "0.62rem",
      letterSpacing: 0.2,
      borderRadius: 999,
      height: 20,
      flexShrink: 0,
      "& .MuiChip-label": { padding: "0 8px" },
      "& .MuiChip-icon": { fontSize: 12, marginInlineStart: 4, marginInlineEnd: -2 },
    },
    // Prominent display price block.
    priceBlock: {
      padding: `${SP.xs}px ${SP.md}px ${SP.md}px ${SP.md}px`,
      display: "flex",
      alignItems: "baseline",
      gap: 4,
      flexWrap: "wrap",
      [theme.breakpoints.down("xs")]: {
        padding: `${SP.xs}px ${SP.md}px ${SP.md}px ${SP.md}px`,
      },
    },
    priceCurrency: {
      fontSize: 18,
      color: fgMuted,
      fontWeight: 600,
      lineHeight: 1,
    },
    priceValue: {
      fontSize: 32,
      fontWeight: 700,
      lineHeight: 1,
      color: fg,
      letterSpacing: "-0.025em",
      fontFeatureSettings: '"tnum" 1',
      [theme.breakpoints.down("xs")]: {
        fontSize: 24,
      },
    },
    priceMonth: {
      fontSize: 13,
      color: fgMuted,
      marginInlineStart: 2,
      fontWeight: 500,
    },
    // Feature list with hairline dividers between each item.
    featureList: {
      padding: `0 ${SP.md}px`,
      display: "flex",
      flexDirection: "column",
      borderTop: `1px solid ${hair}`,
    },
    featureRow: {
      display: "flex",
      alignItems: "center",
      gap: SP.sm,
      padding: `${SP.sm}px 0`,
      borderBottom: `1px solid ${hair}`,
      "&:last-child": {
        borderBottom: "none",
      },
    },
    // Checkmark icon in WhatsApp green (no pill background — clean tick).
    featureIcon: {
      width: 20,
      height: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      color: accent,
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: accent,
      },
    },
    featureText: {
      fontSize: 13.5,
      fontWeight: 500,
      color: fg,
      lineHeight: 1.35,
    },
    featureValue: {
      fontWeight: 700,
      color: fg,
    },
    cardActions: {
      padding: SP.md,
      display: "flex",
      gap: SP.xs,
      justifyContent: "flex-end",
      alignItems: "center",
      borderTop: `1px solid ${hair}`,
      marginTop: "auto",
      [theme.breakpoints.down("xs")]: {
        padding: SP.md,
        flexDirection: "column-reverse",
        alignItems: "stretch",
        gap: SP.xs,
      },
    },
    // Primary "Edit plan" CTA — WhatsApp green outline, full-width on xs.
    editPlanBtn: {
      flex: 1,
      minHeight: 40,
      borderRadius: 10,
      border: `1px solid ${accent}`,
      color: accent,
      background: "transparent",
      fontWeight: 700,
      fontSize: 13,
      letterSpacing: 0.2,
      textTransform: "none",
      padding: "8px 14px",
      transition: "background 0.14s ease, color 0.14s ease, border-color 0.14s ease",
      "&:hover": {
        background: accentSoft,
        borderColor: accent,
      },
      "& .MuiSvgIcon-root": {
        fontSize: 16,
        marginInlineEnd: 6,
      },
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        minHeight: TT.primary,
        fontSize: 14,
      },
    },
    // Delete icon-button kept compact, neutral default, red on hover.
    deleteIconBtn: {
      width: TT.min,
      height: TT.min,
      color: fgMuted,
      borderRadius: 10,
      transition: "color 0.14s ease, background 0.14s ease",
      "&:hover": {
        color: "#e25c5c",
        background: isDark ? "rgba(255,107,107,0.08)" : "rgba(220,38,38,0.06)",
      },
      [theme.breakpoints.down("xs")]: {
        alignSelf: "center",
      },
    },
    emptyState: {
      padding: theme.spacing(8, 2),
      textAlign: "center",
      color: isDark ? "rgba(255,255,255,0.55)" : "#94a3b8",
      border: isDark
        ? "1px dashed rgba(46,227,122,0.22)"
        : "1px dashed rgba(37,211,102,0.30)",
      borderRadius: 14,
      background: isDark ? "rgba(46,227,122,0.03)" : "rgba(37,211,102,0.03)",
    },
    formGrid: {
      marginTop: theme.spacing(1),
    },
    fullWidth: { width: "100%" },
    // Plans grid: collapse auto-fill to single column on xs (B.6).
    plansGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
      gap: 12,
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr",
        gap: SP.md,
      },
    },
    // Enterprise static card — visually distinct from DB-backed plans.
    // Dashed accent border + soft brand-tinted background signal "talk to
    // us" rather than self-serve. Never editable/deletable: it's not in
    // the Plan table, lives only in code.
    enterpriseCard: {
      position: "relative",
      borderRadius: 16,
      border: `1.5px dashed ${isDark ? "rgba(46,227,122,0.55)" : "#128c7e"}`,
      background: isDark
        ? "linear-gradient(160deg, rgba(46,227,122,0.06) 0%, rgba(46,227,122,0.02) 100%)"
        : "linear-gradient(160deg, rgba(18,140,126,0.05) 0%, rgba(37,211,102,0.02) 100%)",
      boxShadow: "none",
      transition: "transform 0.18s cubic-bezier(0.16,1,0.3,1), box-shadow 0.18s ease",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: isDark
          ? "0 14px 32px rgba(0,0,0,0.40)"
          : "0 14px 32px rgba(18,140,126,0.12)",
      },
    },
    enterpriseRibbon: {
      position: "absolute",
      top: 12,
      insetInlineEnd: 12,
      background: accent,
      color: "#fff",
      fontSize: 9.5,
      fontWeight: 800,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      borderRadius: 999,
      padding: "3px 9px",
      lineHeight: 1.2,
    },
    enterpriseCtaBtn: {
      flex: 1,
      minHeight: 40,
      borderRadius: 10,
      background: accent,
      color: "#fff",
      fontWeight: 700,
      fontSize: 13,
      letterSpacing: 0.2,
      textTransform: "none",
      padding: "8px 14px",
      transition: "background 0.14s ease",
      "&:hover": {
        background: isDark ? "#1ebe5d" : "#0e7a6e",
      },
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        minHeight: TT.primary,
        fontSize: 14,
      },
    },
    // CTA full-width on xs (B.2 primary).
    addBtnMobile: {
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        minHeight: TT.primary,
      },
    },
    // DialogActions column-reverse on xs (B.5).
    dialogActionsMobile: {
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column-reverse",
        padding: SP.md,
        gap: SP.xs,
        "& > *": {
          width: "100%",
          minHeight: TT.primary,
          marginInlineStart: "0 !important",
        },
        "& > .MuiBox-root": { display: "none" },
      },
    },
  };
});

// Top stripe is now a fixed WhatsApp brand-green per CRM card-stack v3
// pattern; no per-plan rainbow scale.

const formatPrice = (value) => {
  if (value == null || value === "" || isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("he-IL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

function PlanFeatureToggles() {
  const { values, setFieldValue } = useFormikContext();
  const f = (values && values.features) || {};
  const setBool = (key) => (e) => setFieldValue(`features.${key}`, e.target.checked);
  const setNum = (key) => (e) => {
    const raw = e.target.value;
    setFieldValue(
      `features.${key}`,
      raw === "" ? undefined : Math.max(0, Math.floor(Number(raw) || 0))
    );
  };
  const setEnum = (key) => (e) => setFieldValue(`features.${key}`, e.target.value);
  return (
    <Box>
      {PLAN_FEATURE_GROUPS.map((group) => (
        <Box key={group.group} style={{ marginBottom: 10 }}>
          <Typography
            variant="overline"
            color="textSecondary"
            style={{ fontWeight: 700, letterSpacing: 0.6 }}
          >
            {group.group}
          </Typography>
          <Grid container spacing={0} alignItems="center">
            {group.fields.map((field) => {
              if (field.type === "boolean") {
                return (
                  <Grid item xs={12} sm={6} key={field.key}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          size="small"
                          checked={!!f[field.key]}
                          onChange={setBool(field.key)}
                        />
                      }
                      label={
                        <span style={{ fontSize: 13 }}>
                          {field.label}
                          {field.enforced ? (
                            <span style={{ color: "#2ee37a", marginInlineStart: 4 }}>
                              &#9679;
                            </span>
                          ) : null}
                        </span>
                      }
                    />
                  </Grid>
                );
              }
              if (field.type === "number") {
                return (
                  <Grid item xs={6} sm={4} key={field.key} style={{ padding: 4 }}>
                    <TextField
                      label={field.label}
                      type="number"
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={f[field.key] != null ? f[field.key] : ""}
                      onChange={setNum(field.key)}
                      helperText={field.hint || ""}
                    />
                  </Grid>
                );
              }
              return (
                <Grid item xs={12} sm={6} key={field.key} style={{ padding: 4 }}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      label={field.label}
                      value={
                        f[field.key] || (field.options && field.options[0]) || ""
                      }
                      onChange={setEnum(field.key)}
                    >
                      {(field.options || []).map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}
      <Typography variant="caption" color="textSecondary">
        <span style={{ color: "#2ee37a" }}>&#9679;</span> enforced at runtime today;
        the rest are defined now and enforced incrementally.
      </Typography>
    </Box>
  );
}

function PlanDialog({ open, onClose, initialValue, onSubmit, onDelete, loading }) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const isEdit = Boolean(initialValue?.id);

  const [record, setRecord] = useState({
    name: "",
    users: 0,
    connections: 0,
    queues: 0,
    value: 0,
    isPublic: true,
    billingInterval: "monthly",
    currency: "ILS",
    trialDays: 0,
    ...initialValue,
    features: (initialValue && initialValue.features) || {},
  });

  useEffect(() => {
    if (open) {
      setRecord({
        name: "",
        users: 0,
        connections: 0,
        queues: 0,
        value: 0,
        isPublic: true,
        billingInterval: "monthly",
        currency: "ILS",
        trialDays: 0,
        ...(initialValue || {}),
        features: (initialValue && initialValue.features) || {},
      });
    }
  }, [initialValue, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={12}>
          <Box
            className={classes.headerIcon}
            style={{ width: 36, height: 36, borderRadius: 10 }}
          >
            <PlanIcon />
          </Box>
          <Box ml={1.5}>
            <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1.2 }}>
              {isEdit ? "Edit Plan" : "Create Plan"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {isEdit
                ? `Update the limits and pricing for "${initialValue?.name || "this plan"}"`
                : "Define a new pricing tier for companies"}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Formik
        enableReinitialize
        initialValues={record}
        onSubmit={(values, { resetForm }) => {
          setTimeout(() => {
            onSubmit(values);
            resetForm();
          }, 200);
        }}
      >
        {() => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2} className={classes.formGrid}>
                <Grid item xs={12} sm={8}>
                  <Field
                    as={TextField}
                    label="Plan Name"
                    name="name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder="e.g. Pro, Enterprise, Starter"
                    inputProps={MOBILE_INPUT_PROPS}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>Visibility</InputLabel>
                    <Field as={Select} label="Visibility" name="isPublic">
                      <MenuItem value={true}>Public</MenuItem>
                      <MenuItem value={false}>Private</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    label="Price (ILS / month)"
                    name="value"
                    variant="outlined"
                    size="small"
                    fullWidth
                    type="text"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₪</InputAdornment>
                      ),
                    }}
                    inputProps={MOBILE_INPUT_PROPS}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel>Billing</InputLabel>
                        <Field as={Select} label="Billing" name="billingInterval">
                          <MenuItem value="monthly">Monthly</MenuItem>
                          <MenuItem value="yearly">Yearly</MenuItem>
                          <MenuItem value="one_time">One-time</MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Field
                        as={TextField}
                        label="Currency"
                        name="currency"
                        variant="outlined"
                        size="small"
                        fullWidth
                        inputProps={{ maxLength: 3, style: { textTransform: "uppercase" } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Field
                        as={TextField}
                        label="Trial days"
                        name="trialDays"
                        type="number"
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider style={{ margin: "8px 0" }} />
                  <Typography variant="overline" color="textSecondary">
                    Limits
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field
                    as={TextField}
                    label="Users"
                    name="users"
                    variant="outlined"
                    size="small"
                    fullWidth
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PeopleIcon style={{ fontSize: 18, color: "#9aa0a6" }} />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={MOBILE_INPUT_PROPS}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field
                    as={TextField}
                    label="Connections"
                    name="connections"
                    variant="outlined"
                    size="small"
                    fullWidth
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ConnectionsIcon
                            style={{ fontSize: 18, color: "#9aa0a6" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={MOBILE_INPUT_PROPS}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field
                    as={TextField}
                    label="Queues"
                    name="queues"
                    variant="outlined"
                    size="small"
                    fullWidth
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <QueuesIcon style={{ fontSize: 18, color: "#9aa0a6" }} />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={MOBILE_INPUT_PROPS}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider style={{ margin: "8px 0" }} />
                  <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 4 }}>
                    Features included in this plan
                  </Typography>
                  <PlanFeatureToggles />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions style={{ padding: 16 }} className={classes.dialogActionsMobile}>
              {isEdit && (
                <Button
                  onClick={() => onDelete(initialValue)}
                  color="secondary"
                  startIcon={<DeleteIcon />}
                >
                  Delete
                </Button>
              )}
              <Box flex={1} />
              <Button onClick={onClose}>Cancel</Button>
              <ButtonWithSpinner
                type="submit"
                variant="contained"
                color="primary"
                loading={loading}
              >
                {isEdit ? "Save changes" : "Create plan"}
              </ButtonWithSpinner>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

function FeatureRow({ label, value, classes }) {
  return (
    <Box className={classes.featureRow}>
      <Box className={classes.featureIcon}>
        <CheckIcon />
      </Box>
      <Typography className={classes.featureText} component="span">
        <span className={classes.featureValue}>{value}</span> {label}
      </Typography>
    </Box>
  );
}

const planFeatureBadges = (f) => {
  if (!f) return [];
  const out = [];
  MARQUEE_BADGES.forEach(([key, short]) => {
    if (f[key]) out.push(short);
  });
  if (f.prioritySupport && f.prioritySupport !== "standard") {
    out.push(
      f.prioritySupport === "dedicated" ? "Dedicated support" : "Priority support"
    );
  }
  return out;
};

function PlanCard({ plan, onEdit, onDelete }) {
  const classes = useStyles();
  const isUnlimited = (plan.users || 0) >= 100 && (plan.queues || 0) >= 100;
  // Derive a tier label from the plan name. Falls back to "PLAN" so the
  // accent meta line always reads as a tier eyebrow above the title.
  const tierLabel = (plan.name || "plan").toString().trim().toUpperCase();

  return (
    <Card
      className={`${classes.planCard} ${isUnlimited ? classes.planCardHighlight : ""}`}
      elevation={0}
    >
      <Box className={classes.planTopBar} />
      <Box className={classes.planHeader}>
        <Box className={classes.planHeaderBody}>
          <span className={classes.planTier}>{tierLabel}</span>
          <Typography className={classes.planName} component="div">
            {plan.name || "—"}
          </Typography>
        </Box>
        <Chip
          size="small"
          icon={
            plan.isPublic ? (
              <PublicIcon style={{ fontSize: 14 }} />
            ) : (
              <PrivateIcon style={{ fontSize: 14 }} />
            )
          }
          label={plan.isPublic
            ? i18n.t("settings.plans.public", { defaultValue: "Public" })
            : i18n.t("settings.plans.private", { defaultValue: "Private" })}
          className={classes.visibilityChip}
          style={{
            backgroundColor: plan.isPublic ? "rgba(37, 211, 102, 0.14)" : "rgba(148, 163, 184, 0.18)",
            color: plan.isPublic ? "#128c7e" : "#64748b",
            border: plan.isPublic
              ? "1px solid rgba(37, 211, 102, 0.28)"
              : "1px solid rgba(148, 163, 184, 0.32)",
          }}
        />
      </Box>

      <Box className={classes.priceBlock}>
        {plan.value != null && Number(plan.value) > 0 ? (
          <>
            <span className={classes.priceCurrency}>{currencySymbol(plan.currency)}</span>
            <span className={classes.priceValue}>{formatPrice(plan.value)}</span>
            <span style={{ fontSize: 13, color: "#94a3b8", marginInlineStart: 4 }}>
              {intervalSuffix(plan.billingInterval)}
            </span>
          </>
        ) : (
          <span className={classes.priceValue} style={{ fontSize: 22 }}>
            {i18n.t("settings.plans.customPricing", { defaultValue: "Custom pricing" })}
          </span>
        )}
      </Box>

      <Box className={classes.featureList}>
        <FeatureRow
          label={i18n.t("settings.plans.users", { defaultValue: "users" })}
          value={plan.users || 0}
          classes={classes}
        />
        <FeatureRow
          label={i18n.t("settings.plans.connections", { defaultValue: "WhatsApp connections" })}
          value={plan.connections || 0}
          classes={classes}
        />
        <FeatureRow
          label={i18n.t("settings.plans.queues", { defaultValue: "queues" })}
          value={plan.queues || 0}
          classes={classes}
        />
      </Box>

      {planFeatureBadges(plan.features).length > 0 && (
        <Box
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            padding: "0 4px 10px",
          }}
        >
          {planFeatureBadges(plan.features).map((b) => (
            <Chip
              key={b}
              size="small"
              label={b}
              style={{
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                backgroundColor: "rgba(37, 211, 102, 0.12)",
                color: "#128c7e",
              }}
            />
          ))}
        </Box>
      )}

      <Box className={classes.cardActions}>
        <Button
          className={classes.editPlanBtn}
          onClick={() => onEdit(plan)}
          startIcon={<EditIcon />}
        >
          {i18n.t("settings.plans.editPlan", { defaultValue: "Edit plan" })}
        </Button>
        <Tooltip title={i18n.t("settings.plans.deleteTooltip", { defaultValue: "Delete plan" })}>
          <IconButton
            className={classes.deleteIconBtn}
            onClick={() => onDelete(plan)}
            data-variant="danger"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Card>
  );
}

// Always-shown Enterprise card. Not backed by the Plan table — it's a
// static "contact sales" pitch rendered after the dynamic admin plans.
// Cannot be edited or deleted because it has no DB row.
function EnterpriseCard() {
  const classes = useStyles();
  return (
    <Card className={classes.enterpriseCard} elevation={0}>
      <Box className={classes.planTopBar} style={{ background: "transparent" }} />
      <span className={classes.enterpriseRibbon}>
        {i18n.t("enterprisePlan.title", { defaultValue: "Enterprise" })}
      </span>
      <Box className={classes.planHeader}>
        <Box className={classes.planHeaderBody}>
          <span className={classes.planTier}>
            {i18n.t("enterprisePlan.title", { defaultValue: "Enterprise" })}
          </span>
          <Typography className={classes.planName} component="div">
            {i18n.t("enterprisePlan.title", { defaultValue: "Enterprise" })}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.priceBlock}>
        <span className={classes.priceValue} style={{ fontSize: 22 }}>
          {i18n.t("enterprisePlan.pricing", { defaultValue: "Custom pricing" })}
        </span>
      </Box>

      <Box className={classes.featureList}>
        <Box className={classes.featureRow}>
          <Box className={classes.featureIcon}>
            <CheckIcon />
          </Box>
          <Typography className={classes.featureText} component="span">
            {i18n.t("enterprisePlan.description", {
              defaultValue: "Tailored plan for high-volume teams",
            })}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.cardActions}>
        <Button
          className={classes.enterpriseCtaBtn}
          component="a"
          href="mailto:sales@whapiz.com?subject=Enterprise%20plan%20enquiry"
        >
          {i18n.t("enterprisePlan.ctaContactSales", {
            defaultValue: "Contact Sales",
          })}
        </Button>
      </Box>
    </Card>
  );
}

export default function PlansManager() {
  const classes = useStyles();
  const { list, save, update, remove } = usePlans();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    visibility: "all",
  });

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const planList = await list();
      setRecords(planList || []);
    } catch (e) {
      toast.error(i18n.t("plansManager.toasts.loadError"));
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    const payload = {
      id: data.id,
      connections: Number(data.connections) || 0,
      name: data.name,
      queues: Number(data.queues) || 0,
      users: Number(data.users) || 0,
      value:
        typeof data.value === "string"
          ? data.value.replace(",", ".")
          : data.value || 0,
      isPublic: data.isPublic,
      features: data.features || {},
      billingInterval: data.billingInterval,
      currency: data.currency,
      trialDays: Number(data.trialDays) || 0,
    };
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(payload);
      } else {
        await save(payload);
      }
      await loadPlans();
      setDialogOpen(false);
      setEditingRecord(null);
      toast.success(i18n.t("plansManager.toasts.success"));
    } catch (e) {
      toast.error(i18n.t("plansManager.toasts.saveError"));
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setLoading(true);
    try {
      await remove(pendingDelete.id);
      await loadPlans();
      setShowConfirmDialog(false);
      setPendingDelete(null);
      setDialogOpen(false);
      setEditingRecord(null);
      toast.success(i18n.t("plansManager.toasts.success"));
    } catch (e) {
      toast.error(i18n.t("plansManager.toasts.deleteError"));
    }
    setLoading(false);
  };

  const openCreate = () => {
    setEditingRecord({
      name: "",
      users: 0,
      connections: 0,
      queues: 0,
      value: 0,
      isPublic: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingRecord({
      id: row.id,
      name: row.name || "",
      users: row.users || 0,
      connections: row.connections || 0,
      queues: row.queues || 0,
      value: row.value != null ? row.value : 0,
      isPublic: row.isPublic,
      billingInterval: row.billingInterval,
      currency: row.currency,
      trialDays: row.trialDays != null ? row.trialDays : 0,
      features: row.features || {},
    });
    setDialogOpen(true);
  };

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return records.filter((r) => {
      if (filters.visibility === "public" && !r.isPublic) return false;
      if (filters.visibility === "private" && r.isPublic) return false;
      if (q && !(r.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [records, filters]);

  const stats = useMemo(() => {
    return {
      total: records.length,
      publicCount: records.filter((r) => r.isPublic).length,
    };
  }, [records]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <span className={classes.headerIcon}>
            <PlanIcon />
          </span>
          <Box>
            <Typography variant="h5" className={classes.headerTitle} component="div">
              {i18n.t("settings.Plans.title", { defaultValue: "Plans" })}
              <span className={classes.countChip}>{stats.total}</span>
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {i18n.t("settings.plans.subtitle", {
                publicCount: stats.publicCount,
                privateCount: stats.total - stats.publicCount,
                defaultValue: `${stats.publicCount} public · ${stats.total - stats.publicCount} private`,
              })}
            </Typography>
          </Box>
        </Box>
        <Button
          className={`${classes.addBtn} ${classes.addBtnMobile}`}
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          {i18n.t("settings.plans.add", { defaultValue: "Add Plan" })}
        </Button>
      </Box>

      <Paper className={classes.searchBar} elevation={0}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              className={classes.inputControl}
              placeholder={i18n.t("settings.plans.searchPlaceholder", { defaultValue: "Search by plan name..." })}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small" variant="outlined" className={classes.inputControl}>
              <InputLabel>{i18n.t("settings.plans.visibilityLabel", { defaultValue: "Visibility" })}</InputLabel>
              <Select
                label={i18n.t("settings.plans.visibilityLabel", { defaultValue: "Visibility" })}
                value={filters.visibility}
                onChange={(e) =>
                  setFilters({ ...filters, visibility: e.target.value })
                }
              >
                <MenuItem value="all">{i18n.t("settings.plans.allPlans", { defaultValue: "All plans" })}</MenuItem>
                <MenuItem value="public">{i18n.t("settings.plans.publicOnly", { defaultValue: "Public only" })}</MenuItem>
                <MenuItem value="private">{i18n.t("settings.plans.privateOnly", { defaultValue: "Private only" })}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setFilters({ search: "", visibility: "all" })}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {filtered.length === 0 ? (
        <>
          <Box className={classes.emptyState}>
            <PlanIcon style={{ fontSize: 56, opacity: 0.3 }} />
            <Typography variant="body1" style={{ marginTop: 12 }}>
              {records.length === 0 ? "No plans created yet" : "No plans match your filters"}
            </Typography>
            <Typography variant="body2" style={{ marginTop: 4 }}>
              {records.length === 0
                ? 'Click "Add Plan" to create your first pricing tier.'
                : "Try changing the search or visibility filter."}
            </Typography>
          </Box>
          {/* Always-shown Enterprise card — sits alone when no DB plans exist. */}
          <div className={classes.plansGrid} style={{ marginTop: 16 }}>
            <EnterpriseCard />
          </div>
        </>
      ) : (
        <div
          // CSS-grid auto-fill so cards stack denser on wide screens
          // (fits 5–6 per row instead of the old 4) and gracefully
          // wrap on narrow viewports. 12px gutter keeps them tight.
          // On xs collapses to 1 col via classes.plansGrid (spec B.6).
          className={classes.plansGrid}
        >
          {filtered.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onEdit={openEdit}
              onDelete={(p) => {
                setPendingDelete(p);
                setShowConfirmDialog(true);
              }}
            />
          ))}
          {/* Always-shown Enterprise card. Renders AFTER admin plans so
              dynamic tiers come first and Enterprise reads as "talk to
              us". Not in DB, so no edit/delete affordance — see
              EnterpriseCard component above. */}
          <EnterpriseCard />
        </div>
      )}

      <PlanDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingRecord(null);
        }}
        initialValue={editingRecord || {}}
        onSubmit={handleSubmit}
        onDelete={(rec) => {
          setPendingDelete(rec);
          setShowConfirmDialog(true);
        }}
        loading={loading}
      />

      <ConfirmationModal
        title="Delete plan"
        open={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setPendingDelete(null);
        }}
        onConfirm={handleDelete}
      >
        Are you sure you want to permanently delete{" "}
        <b>{pendingDelete?.name || "this plan"}</b>? Companies currently on this plan
        may be affected.
      </ConfirmationModal>
    </Box>
  );
}
